import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, Save, Settings2 } from "lucide-react";

const STATUSES = ["received", "under_review", "accepted", "rejected", "completed"];
const STATUS_COLORS = {
  received:     "bg-gold/20 text-gold",
  under_review: "bg-sky-500/20 text-sky-300",
  accepted:     "bg-violet-500/20 text-violet-300",
  rejected:     "bg-rose-500/20 text-rose-300",
  completed:    "bg-emerald-500/20 text-emerald-300",
};
const STATUS_LABEL = {
  received: "Received", under_review: "Under Review", accepted: "Accepted",
  rejected: "Rejected", completed: "Completed",
};
const CONDITION_LABEL = { like_new: "Like New", good: "Good", fair: "Fair", poor: "Poor" };

const TRADEIN_DEVICES = [
  { id: "ps5",           label: "PlayStation 5",         category: "console" },
  { id: "ps4_pro",       label: "PlayStation 4 Pro",     category: "console" },
  { id: "ps4",           label: "PlayStation 4",         category: "console" },
  { id: "switch_oled",   label: "Nintendo Switch OLED",  category: "console" },
  { id: "switch",        label: "Nintendo Switch",       category: "console" },
  { id: "switch_lite",   label: "Nintendo Switch Lite",  category: "console" },
  { id: "xbox_series_x", label: "Xbox Series X",         category: "console" },
  { id: "xbox_series_s", label: "Xbox Series S",         category: "console" },
  { id: "ps5_dualsense", label: "PS5 DualSense",         category: "controller" },
  { id: "ps4_dualshock", label: "PS4 DualShock 4",       category: "controller" },
  { id: "xbox_controller",label: "Xbox Controller",      category: "controller" },
  { id: "joycon",        label: "Joy-Con (pair)",        category: "controller" },
  { id: "pro_controller",label: "Nintendo Pro Controller",category: "controller" },
];

const CATEGORY_LABEL = { console: "Console", controller: "Controller", games: "Games" };

export default function AdminTradeIn() {
  const [view, setView] = useState("list");
  const [enquiries, setEnquiries] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ status: "", final_value: "", estimate_min: "", estimate_max: "", admin_notes: "" });
  const [saving, setSaving] = useState(false);
  const [pricingDraft, setPricingDraft] = useState({});
  const [savingPricing, setSavingPricing] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/tradein/enquiries");
    setEnquiries(r.data);
  };

  const loadPricing = async () => {
    const r = await api.get("/admin/tradein/pricing");
    setPricingDraft(r.data);
  };

  useEffect(() => { load(); loadPricing(); }, []);

  function openEnquiry(e) {
    setActive(e);
    setForm({ status: e.status, final_value: e.final_value ?? "", estimate_min: e.estimate_min ?? "", estimate_max: e.estimate_max ?? "", admin_notes: e.admin_notes ?? "" });
  }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.final_value !== "") payload.final_value = parseFloat(payload.final_value);
      else delete payload.final_value;
      if (payload.estimate_min !== "") payload.estimate_min = parseFloat(payload.estimate_min);
      else delete payload.estimate_min;
      if (payload.estimate_max !== "") payload.estimate_max = parseFloat(payload.estimate_max);
      else delete payload.estimate_max;
      await api.patch(`/admin/tradein/enquiries/${active.id}`, payload);
      toast.success("Updated");
      setActive(null);
      await load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  function setPriceField(deviceId, field, value) {
    setPricingDraft((p) => ({ ...p, [deviceId]: { ...(p[deviceId] ?? {}), [field]: value ? parseFloat(value) : "" } }));
  }

  async function savePricing() {
    setSavingPricing(true);
    try {
      await api.put("/admin/tradein/pricing", pricingDraft);
      toast.success("Pricing saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingPricing(false); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Buyback</div>
          <h1 className="text-3xl font-black uppercase tracking-tighter">Trade-In Enquiries</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setView("list")} className={`text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg transition ${view === "list" ? "bg-gold text-navy-900" : "border border-gold/30 text-gold hover:bg-gold/10"}`}>Enquiries</button>
          <button onClick={() => setView("pricing")} className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg transition ${view === "pricing" ? "bg-gold text-navy-900" : "border border-gold/30 text-gold hover:bg-gold/10"}`}><Settings2 className="w-3.5 h-3.5" /> Pricing</button>
        </div>
      </div>

      {view === "list" && (
        <>
          <div className="bg-navy-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gold/10 text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left px-5 py-3">Enquiry</th>
                  <th className="text-left px-5 py-3">Customer</th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">Category</th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">Item</th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">Mode</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3 hidden lg:table-cell">Value</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {enquiries.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-slate-500 text-sm">No trade-in enquiries yet</td></tr>
                )}
                {enquiries.map((e) => (
                  <tr key={e.id} className="border-b border-gold/5 hover:bg-navy-700/40 transition">
                    <td className="px-5 py-3 font-mono text-xs text-gold">{e.enquiry_number}</td>
                    <td className="px-5 py-3">
                      <div className="font-bold text-xs">{e.customer_name}</div>
                      <div className="text-[10px] text-slate-500">{e.customer_phone}</div>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gold">{CATEGORY_LABEL[e.category] ?? e.category}</span>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell">
                      <div className="text-xs">{e.device_label}</div>
                      <div className="text-[10px] text-slate-500">{CONDITION_LABEL[e.condition] ?? "—"}</div>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell text-xs text-slate-400">
                      {e.preferred_mode === "store_credit" ? "Store Credit" : "UPI"}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_COLORS[e.status] ?? "bg-slate-700 text-slate-300"}`}>
                        {STATUS_LABEL[e.status] ?? e.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 hidden lg:table-cell text-xs text-slate-300">
                      {e.final_value != null ? `₹${e.final_value.toLocaleString("en-IN")}` : (e.estimate_min != null ? `~₹${e.estimate_min.toLocaleString("en-IN")}–₹${e.estimate_max?.toLocaleString("en-IN")}` : "—")}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => openEnquiry(e)} className="text-xs font-bold text-gold hover:underline">Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {active && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
              <div className="bg-navy-800 border border-gold/20 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-gold">{active.enquiry_number}</div>
                    <div className="text-lg font-black mt-1">{active.device_label}</div>
                    <div className="text-sm text-slate-400">{CATEGORY_LABEL[active.category] ?? active.category}{active.condition ? ` · ${CONDITION_LABEL[active.condition]}` : ""} · {active.preferred_mode === "store_credit" ? "Store Credit" : `UPI — ${active.upi_id}`}</div>
                  </div>
                  <button onClick={() => setActive(null)}><X className="w-5 h-5 text-slate-400 hover:text-white" /></button>
                </div>
                <div className="text-xs text-slate-400 space-y-1 mb-5 bg-navy-700/50 rounded-lg p-4">
                  <p><strong className="text-slate-300">Customer:</strong> {active.customer_name}</p>
                  <p><strong className="text-slate-300">Email:</strong> {active.customer_email}</p>
                  <p><strong className="text-slate-300">Phone:</strong> {active.customer_phone}</p>
                  {active.accessories_included && <p><strong className="text-slate-300">Accessories:</strong> {active.accessories_included}</p>}
                  <p><strong className="text-slate-300">Delivery:</strong> Drop-off at store</p>
                  {active.notes && <p><strong className="text-slate-300">Notes:</strong> {active.notes}</p>}
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Status</label>
                    <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white">
                      {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Estimate Min (₹)</label>
                      <input type="number" value={form.estimate_min} onChange={(e) => setForm((f) => ({ ...f, estimate_min: e.target.value }))} className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white" placeholder="0" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Estimate Max (₹)</label>
                      <input type="number" value={form.estimate_max} onChange={(e) => setForm((f) => ({ ...f, estimate_max: e.target.value }))} className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white" placeholder="0" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Final Confirmed Value (₹)</label>
                    <input type="number" value={form.final_value} onChange={(e) => setForm((f) => ({ ...f, final_value: e.target.value }))} className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white" placeholder="0" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Admin Notes (sent to customer)</label>
                    <textarea value={form.admin_notes} onChange={(e) => setForm((f) => ({ ...f, admin_notes: e.target.value }))} rows={3} className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white resize-none" />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setActive(null)} className="border border-gold/30 text-gold text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full hover:bg-gold/10 transition">Cancel</button>
                  <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-gold text-navy-900 text-xs font-extrabold uppercase tracking-wider px-6 py-2.5 rounded-full hover:bg-gold-light transition disabled:opacity-50">
                    <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save & Notify"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {view === "pricing" && (
        <div>
          <p className="text-sm text-slate-400 mb-6">Set base estimate ranges (Like New). Condition multipliers applied automatically: Good=80%, Fair=60%, Poor=40%. Games pricing is handled per-enquiry via email.</p>
          {["console", "controller"].map((cat) => (
            <div key={cat} className="mb-8">
              <div className="text-xs font-bold uppercase tracking-wider text-gold mb-4">{CATEGORY_LABEL[cat]}</div>
              <div className="grid sm:grid-cols-2 gap-4">
                {TRADEIN_DEVICES.filter((d) => d.category === cat).map((dev) => {
                  const p = pricingDraft[dev.id] ?? {};
                  return (
                    <div key={dev.id} className="bg-navy-700 rounded-xl p-4 border border-gold/10">
                      <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">{dev.label}</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Base Min (₹)</label>
                          <input type="number" value={p.min ?? ""} onChange={(e) => setPriceField(dev.id, "min", e.target.value)} className="w-full bg-navy-800 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white" placeholder="0" />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Base Max (₹)</label>
                          <input type="number" value={p.max ?? ""} onChange={(e) => setPriceField(dev.id, "max", e.target.value)} className="w-full bg-navy-800 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white" placeholder="0" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <button onClick={savePricing} disabled={savingPricing} className="flex items-center gap-2 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-8 py-3 rounded-full hover:bg-gold-light transition disabled:opacity-50">
            <Save className="w-4 h-4" /> {savingPricing ? "Saving…" : "Save Pricing"}
          </button>
        </div>
      )}
    </div>
  );
}
